import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

// text-mushaf is a font-size token (fluid mushaf size), not a text colour.
const twMerge = extendTailwindMerge({
  extend: { classGroups: { "font-size": [{ text: ["mushaf"] }] } },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const pad3 = (n: number) => String(n).padStart(3, "0");

/** Arabic-Indic digits, for ornament only (functional numbers stay Latin). */
export const toArabicDigits = (n: number) => n.toLocaleString("ar-EG");

/** Extracts the 11-character id from a bare YouTube id or any common YouTube URL. */
export function youtubeId(input: unknown): string | null {
  const value = String(input ?? "").trim();
  if (!value) return null;
  const match = value.match(
    /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/))([\w-]{11})/,
  );
  if (match) return match[1];
  return /^[\w-]{11}$/.test(value) ? value : null;
}

/** Glyph for surah N in the "surah-name" font (U+E000 + N). */
export const surahNameGlyph = (n: number) =>
  Number.isInteger(n) && n >= 1 && n <= 114 ? String.fromCodePoint(0xe000 + n) : "";

/** The word سورة in the "surah-name" font. */
export const SURAH_WORD_GLYPH = "\uE000";

/** Madinah surah-header frame in the "quran-common" font. */
export const SURAH_FRAME_GLYPH = "\uE000";

/**
 * Surah number from a word's audio_url ("wbw/002_025_001.mp3"), or null for
 * words without audio (ayah-end markers).
 */
export function surahFromAudioUrl(audioUrl: string | null | undefined) {
  const n = Number(audioUrl?.slice(4, 7));
  return Number.isInteger(n) && n >= 1 && n <= 114 ? n : null;
}

export const surahsNamesMap = new Map([
  [1, "Al-Fatiha"],
  [2, "Al-Baqarah"],
  [3, "Aali Imran"],
  [4, "An-Nisa"],
  [5, "Al-Ma'idah"],
  [6, "Al-An'am"],
  [7, "Al-A'raf"],
  [8, "Al-Anfal"],
  [9, "At-Tawbah"],
  [10, "Yunus"],
  [11, "Hud"],
  [12, "Yusuf"],
  [13, "Ar-Ra'd"],
  [14, "Ibrahim"],
  [15, "Al-Hijr"],
  [16, "An-Nahl"],
  [17, "Al-Isra"],
  [18, "Al-Kahf"],
  [19, "Maryam"],
  [20, "Ta-Ha"],
  [21, "Al-Anbiya"],
  [22, "Al-Hajj"],
  [23, "Al-Mu'minun"],
  [24, "An-Nur"],
  [25, "Al-Furqan"],
  [26, "Ash-Shu'ara"],
  [27, "An-Naml"],
  [28, "Al-Qasas"],
  [29, "Al-Ankabut"],
  [30, "Ar-Rum"],
  [31, "Luqman"],
  [32, "As-Sajda"],
  [33, "Al-Ahzab"],
  [34, "Saba"],
  [35, "Fatir"],
  [36, "Ya-Sin"],
  [37, "As-Saffat"],
  [38, "Sad"],
  [39, "Az-Zumar"],
  [40, "Ghafir"],
  [41, "Fussilat"],
  [42, "Ash-Shura"],
  [43, "Az-Zukhruf"],
  [44, "Ad-Dukhan"],
  [45, "Al-Jathiya"],
  [46, "Al-Ahqaf"],
  [47, "Muhammad"],
  [48, "Al-Fath"],
  [49, "Al-Hujurat"],
  [50, "Qaf"],
  [51, "Adh-Dhariyat"],
  [52, "At-Tur"],
  [53, "An-Najm"],
  [54, "Al-Qamar"],
  [55, "Ar-Rahman"],
  [56, "Al-Waqi'a"],
  [57, "Al-Hadid"],
  [58, "Al-Mujadila"],
  [59, "Al-Hashr"],
  [60, "Al-Mumtahina"],
  [61, "As-Saff"],
  [62, "Al-Jumu'a"],
  [63, "Al-Munafiqoon"],
  [64, "At-Taghabun"],
  [65, "At-Tahrim"],
  [66, "At-Tahreem"],
  [67, "Al-Mulk"],
  [68, "Al-Qalam"],
  [69, "Al-Haaqqa"],
  [70, "Al-Ma'arij"],
  [71, "Nuh"],
  [72, "Al-Jinn"],
  [73, "Al-Muzzammil"],
  [74, "Al-Muddathir"],
  [75, "Al-Qiyama"],
  [76, "Al-Insan"],
  [77, "Al-Mursalat"],
  [78, "An-Naba"],
  [79, "An-Nazi'at"],
  [80, "Abasa"],
  [81, "At-Takwir"],
  [82, "Al-Infitar"],
  [83, "Al-Mutaffifin"],
  [84, "Al-Inshiqaq"],
  [85, "Al-Buruj"],
  [86, "At-Tariq"],
  [87, "Al-Ala"],
  [88, "Al-Ghashiya"],
  [89, "Al-Fajr"],
  [90, "Al-Balad"],
  [91, "Ash-Shams"],
  [92, "Al-Lail"],
  [93, "Ad-Duha"],
  [94, "Ash-Sharh"],
  [95, "At-Tin"],
  [96, "Al-Alaq"],
  [97, "Al-Qadr"],
  [98, "Al-Bayyina"],
  [99, "Az-Zalzalah"],
  [100, "Al-Adiyat"],
  [101, "Al-Qaria"],
  [102, "At-Takathur"],
  [103, "Al-Asr"],
  [104, "Al-Humazah"],
  [105, "Al-Fil"],
  [106, "Quraish"],
  [107, "Al-Ma'un"],
  [108, "Al-Kawthar"],
  [109, "Al-Kafirun"],
  [110, "An-Nasr"],
  [111, "Al-Masad"],
  [112, "Al-Ikhlas"],
  [113, "Al-Falaq"],
  [114, "An-Nas"],
]);
